import { AppPage } from '@hooks/useNavigation';
import type { RoundDuration } from '@hooks/useSettings';

import { Header } from './shared/Header';

// Access the auto-generated build date
declare const __BUILD_DATE__: string;
const BUILD_DATE =
  typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : 'Development';

interface SettingsScreenProps {
  showSideMenu: boolean;
  toggleSideMenu: () => void;
  advancedMode: boolean;
  setAdvancedMode: (mode: boolean) => void;
  totalPositions: number;
  onNavigate: (page: AppPage) => void;
  animatePositionsPill?: boolean;
  balance?: number;
  extraMode?: boolean;
  setExtraMode?: (mode: boolean) => void;
  roundDuration?: RoundDuration;
  setRoundDuration?: (duration: RoundDuration) => void;
  timeshiftOffsetHours?: number;
  setTimeshiftOffsetHours?: (hours: number) => void;
}

export const SettingsScreen = ({
  advancedMode,
  setAdvancedMode,
  onNavigate,
  extraMode = true,
  setExtraMode,
  roundDuration = '1d',
  setRoundDuration,
  timeshiftOffsetHours = -24,
  setTimeshiftOffsetHours,
}: SettingsScreenProps) => {
  const durationOptions: { value: RoundDuration; label: string }[] = [
    { value: '1m', label: '1 Minute' },
    { value: '5m', label: '5 Minutes' },
    { value: '15m', label: '15 Minutes' },
    { value: '30m', label: '30 Minutes' },
    { value: '1h', label: '1 Hour' },
    { value: '2h', label: '2 Hours' },
    { value: '4h', label: '4 Hours' },
    { value: '8h', label: '8 Hours' },
    { value: '12h', label: '12 Hours' },
    { value: '1d', label: '1 Day' },
  ];
  return (
    <div className="bg-brand-dark min-h-screen w-full flex flex-col">
      <Header asset="SETTINGS" title="SETTINGS" onNavigate={onNavigate} />

      <div className="flex-1 w-full p-4 space-y-4">
        {/* Greek Mode Setting */}
        <div className="bg-white/5 rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white text-[16px] font-medium">Greek Mode</h3>
              <p className="text-[#808080] text-[14px] mt-1">
                Show advanced options terminology (CALL/PUT vs ABOVE/BELOW)
              </p>
            </div>

            <button
              onClick={() => setAdvancedMode(!advancedMode)}
              className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
                advancedMode ? 'bg-brand-green' : 'bg-gray-600'
              }`}
            >
              <div
                className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-300 ${
                  advancedMode ? 'translate-x-6' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Round Duration Setting */}
        <div className="bg-white/5 rounded-2xl p-4">
          <div className="mb-3">
            <h3 className="text-white text-[16px] font-medium">
              Round Duration
            </h3>
            <p className="text-[#808080] text-[14px] mt-1">
              How often options expire (shorter durations are for testing)
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {durationOptions.map(option => (
              <button
                key={option.value}
                onClick={() => setRoundDuration?.(option.value)}
                className={`p-3 rounded-lg text-sm font-medium transition-colors ${
                  roundDuration === option.value
                    ? 'bg-brand-green text-black'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="mb-2">
              <label className="text-white text-[14px] font-medium">
                Timeshift Offset (hours)
              </label>
              <p className="text-[#808080] text-[12px] mt-1">
                Shift all prices and options back in time for demo purposes. Negative values = past.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={timeshiftOffsetHours}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (!isNaN(value)) {
                    setTimeshiftOffsetHours?.(value);
                  }
                }}
                className="flex-1 bg-white/10 text-white px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-green"
                placeholder="-24"
              />
              <span className="text-[#808080] text-sm">hours</span>
            </div>
          </div>
        </div>

        {/* Extra Mode Setting */}
        <div className="bg-white/5 rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white text-[16px] font-medium">Extra Mode</h3>
              <p className="text-[#808080] text-[14px] mt-1">
                Enable experimental features and enhanced data (currently does
                nothing)
              </p>
            </div>

            <button
              onClick={() => setExtraMode?.(!extraMode)}
              className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
                extraMode ? 'bg-brand-green' : 'bg-gray-600'
              }`}
            >
              <div
                className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-300 ${
                  extraMode ? 'translate-x-6' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        </div>

        {/* About Section */}
        <div className="bg-white/5 rounded-2xl p-4">
          <h3 className="text-white text-[16px] font-medium mb-3">About</h3>
          <div className="space-y-2 text-[14px]">
            <div className="flex justify-between">
              <span className="text-[#808080]">Version</span>
              <span className="text-white">{BUILD_DATE}</span>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="bg-white/5 rounded-2xl p-4">
          <h3 className="text-white text-[16px] font-medium mb-3">
            Help & Support
          </h3>
          <div className="space-y-3">
            <button className="w-full text-left text-[#808080] hover:text-white transition-colors">
              Documentation
            </button>
            <button className="w-full text-left text-[#808080] hover:text-white transition-colors">
              Contact Support
            </button>
            <button className="w-full text-left text-[#808080] hover:text-white transition-colors">
              Report Bug
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
